import React, { useState } from "react";
import Dropzone from "react-dropzone";
import {
  Field,
  FieldProps,
  FormikErrors,
  FormikHandlers,
  FormikTouched,
} from "formik";

import { Error, Radio } from "../input";
import ProgressUploader from "./ProgressUploader";
import EyePositionHeading from "./EyePositionHeading";
import { EnrolmentValues, ResearchParticipantValues } from "./types";
import uploadImage from "./uploadImages";

type UploadedFiles = Record<
  string,
  { percentCompleted: number; path?: string }
>;

type SetFieldValue = FieldProps["form"]["setFieldValue"];

type ResearchParticipantProps = {
  csrfToken: string;
  values: ResearchParticipantValues;
  setFieldValue: SetFieldValue;
  setIsUploading: (value: boolean) => void;
  touched: FormikTouched<ResearchParticipantValues>;
  errors: FormikErrors<ResearchParticipantValues>;
  handleChange: FormikHandlers["handleChange"];
  handleBlur: FormikHandlers["handleBlur"];
};

type Side = "left" | "right";

type EnrolmentValuesPick<T extends keyof EnrolmentValues> = keyof Pick<
  EnrolmentValues,
  T
>;

type VisualAcuity = EnrolmentValuesPick<`visual_acuity_${Side}`>;
type VisualAcuityModifier =
  EnrolmentValuesPick<`visual_acuity_${Side}_normal_with_modifier`>;

type DiabeticRetinopathy = EnrolmentValuesPick<`diabetic_retinopathy_${Side}`>;

type MacularOedema = EnrolmentValuesPick<`macular_oedema_${Side}`>;

type ImageField = "left_eye_image";

function ResearchParticipant({
  csrfToken,
  values,
  setIsUploading,
  setFieldValue,
  touched,
  errors,
  handleChange,
  handleBlur,
}: ResearchParticipantProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({});

  const errorClass = (field: keyof ResearchParticipantValues) => {
    if (!!errors[field]) {
      return " field_with_errors";
    } else {
      return "";
    }
  };

  const upload = (file: File, field: string, setFieldValue: SetFieldValue) => {
    setUploadedFiles({
      ...uploadedFiles,
      [field]: { percentCompleted: 0 },
    });
    setIsUploading(true);

    const updatePercentage = (percentCompleted: number) => {
      uploadedFiles[field] = { percentCompleted };
      setUploadedFiles({
        ...uploadedFiles,
      });
    };

    const finished = (path: string) => {
      uploadedFiles[field] = { ...uploadedFiles[field], path };
      setUploadedFiles({
        ...uploadedFiles,
      });
      setFieldValue(field, path);
      setIsUploading(false);
    };

    uploadImage(csrfToken, file, updatePercentage, finished);
  };

  const validateEyeImage = (value: undefined | string) =>
    value != undefined ? null : "must be a valid file format";

  const imageInput =
    (label: string = "") =>
    ({ field: { name }, form: { errors, setFieldValue } }: FieldProps) => {
      const error = errors[name];

      return (
        <div className={`form-group ${errorClass(name as ImageField)}`}>
          <Dropzone
            onDropAccepted={(files) => upload(files[0], name, setFieldValue)}
            onDropRejected={() => setFieldValue(name, undefined)}
            multiple={false}
            accept={"image/*, application/pdf"}
          >
            {({
              getRootProps,
              getInputProps,
            }: {
              getRootProps: any;
              getInputProps: any;
            }) => {
              return (
                <div
                  {...getRootProps()}
                  className="dropzone"
                  aria-label={label}
                >
                  <input {...getInputProps()} />
                  {uploadedFiles[name] ? (
                    <ProgressUploader
                      percentage={uploadedFiles[name].percentCompleted}
                      label={name}
                    />
                  ) : (
                    <p className="dropzone-label">{label}</p>
                  )}
                </div>
              );
            }}
          </Dropzone>
          {error && (
            <Error id={`${name}ErrorDescription`} errorText={error as string} />
          )}
        </div>
      );
    };

  const switchStyles = (
    research_participant: EnrolmentValues["research_participant"],
  ) => {
    if (research_participant) {
      return "mdc-switch mdc-switch--checked";
    } else {
      return "mdc-switch";
    }
  };

  const switchText = (
    research_participant: EnrolmentValues["research_participant"],
  ) => {
    return research_participant ? "Yes" : "No";
  };

  const switchRole = (
    research_participant: EnrolmentValues["research_participant"],
  ) => {
    return research_participant ? "true" : "false";
  };

  const renderError = (field: keyof EnrolmentValues) => {
    return (
      touched[field] &&
      errors[field] && (
        <Error
          id={`${field}ErrorDescription`}
          errorText={errors[field] as string}
        />
      )
    );
  };

  const renderVisualAcuity = (side: VisualAcuity) => {
    const modifier: VisualAcuityModifier =
      `${side}_normal_with_modifier` as VisualAcuityModifier;
    const side_label: Side = {
      visual_acuity_right: "right",
      visual_acuity_left: "left",
    }[side] as Side;
    return (
      <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
        <div className="mdc-layout-grid__inner">
          <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-5">
            <div
              className={"form-group" + errorClass(side)}
              data-testid={`${side}_form_group`}
            >
              <label
                className="sr-only"
                htmlFor={side}
                style={{ textTransform: "capitalize" }}
              >
                {side_label} eye visual acuity
              </label>
              <input
                type="text"
                id={side}
                name={side}
                data-testid={side}
                value={values[side]}
                onChange={handleChange}
                onBlur={handleBlur}
                className="input-text border rounded border-gray-600"
              />
              {renderError(side)}
            </div>
          </div>

          <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-1 va-slash">
            /
          </div>

          <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-5">
            <div
              className={"form-group" + errorClass(modifier)}
              id={`${modifier}_form_group`}
              data-testid={`${modifier}_form_group`}
            >
              <label
                className="sr-only"
                htmlFor={modifier}
                style={{ textTransform: "capitalize" }}
              >
                {side_label} eye visual acuity with modifier.
              </label>
              <input
                type="text"
                id={modifier}
                name={modifier}
                data-testid={modifier}
                value={values[modifier]}
                onChange={handleChange}
                onBlur={handleBlur}
                className="input-text border rounded border-gray-600"
              />
              {renderError(modifier)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDiabeticRetinopathy = (side: DiabeticRetinopathy) => {
    const side_label: Side = {
      diabetic_retinopathy_right: "right",
      diabetic_retinopathy_left: "left",
    }[side] as Side;
    return (
      <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
        <div
          className={"form-group" + errorClass(side)}
          data-testid={`${side}_form_group`}
        >
          <label className="sr-only" htmlFor={side}>
            {side_label} eye diabetic retinopathy
          </label>
          <select
            id={side}
            name={side}
            data-testid={side}
            value={values[side]}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-text border rounded border-gray-600"
          >
            <option value={""} disabled>
              Please select
            </option>
            <option value="none">None</option>
            <option value="minimal">Minimal</option>
            <option value="moderate">Mild Moderate NPDR</option>
            <option value="severe">Severe NPDR</option>
            <option value="pdr">PDR</option>
          </select>
          {renderError(side)}
        </div>
      </div>
    );
  };

  const renderMacularOedema = (side: MacularOedema) => {
    const side_label: Side = {
      macular_oedema_right: "right",
      macular_oedema_left: "left",
    }[side] as Side;
    return (
      <fieldset className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
        <legend className="sr-only">Macular oedema {side_label}</legend>
        <div
          className={"form-group" + errorClass(side)}
          data-testid={`${side}_form_group`}
        >
          <div className="input-radio">
            <label className="mdc-form-field">
              <Radio
                id={`${side}_none`}
                name={side}
                label="None"
                value="none"
                checked={values[side] == "none"}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </label>

            <br />

            <label className="mdc-form-field">
              <Radio
                id={`${side}_non_centre`}
                name={side}
                label="Yes, Non-Centre Involving / Vision Threatening"
                value="non-centre-involving"
                checked={values[side] == "non-centre-involving"}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </label>

            <br />

            <label className="mdc-form-field">
              <Radio
                id={`${side}_centre`}
                name={side}
                label="Yes, Centre Involving / Vision Affecting"
                value="centre-involving"
                checked={values[side] == "centre-involving"}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </label>
          </div>
          {renderError(side)}
        </div>
      </fieldset>
    );
  };

  const renderCollapsibleContent = () => {
    if (!values.research_participant) return null;

    return (
      <div className="card-body collapsible-content">
        <fieldset>
          <legend>
            <h2>Examination findings</h2>
          </legend>
          <EyePositionHeading />
          <fieldset className="visual-acuity">
            <div className="mdc-layout-grid__inner">
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                <legend className="examination-label">Visual acuity 6/6</legend>
              </div>
              {renderVisualAcuity("visual_acuity_right")}
              {renderVisualAcuity("visual_acuity_left")}
            </div>
          </fieldset>
          <fieldset className="diabetic-retinopathy">
            <div className="mdc-layout-grid__inner">
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                <legend className="examination-label">
                  Diabetic retinopathy
                </legend>
              </div>
              {renderDiabeticRetinopathy("diabetic_retinopathy_right")}
              {renderDiabeticRetinopathy("diabetic_retinopathy_left")}
            </div>
          </fieldset>
          <fieldset className="macular-oedema-heading">
            <div className="mdc-layout-grid__inner">
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                <legend className="examination-label">Macular Oedema</legend>
              </div>
              {renderMacularOedema("macular_oedema_right")}
              {renderMacularOedema("macular_oedema_left")}
            </div>
          </fieldset>
        </fieldset>

        <fieldset>
          <legend>
            <h2>Images</h2>
          </legend>
          <EyePositionHeading />
          <div className="mdc-layout-grid__inner">
            <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3"></div>
            <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-9">
              <div className="message-text">
                <span>Supported formats: jpeg, png, tiff, pdf</span>
              </div>
            </div>
          </div>
          <div className="retinal-images">
            <div className="mdc-layout-grid__inner">
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                <label className="examination-label">Retinal/Disc photo</label>
              </div>
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                <Field
                  idName="right-eye-image"
                  name="right_eye_image"
                  component={imageInput(
                    "Select right eye retinal/disc photo file or drop here to upload",
                  )}
                  validate={validateEyeImage}
                  label={values.right_eye_image}
                />
              </div>
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                <Field
                  idName="left-eye-image"
                  name="left_eye_image"
                  component={imageInput(
                    "Select left eye retinal/disc photo file or drop here to upload",
                  )}
                  validate={validateEyeImage}
                  label={values.left_eye_image}
                />
              </div>
            </div>
          </div>
          <div className="oct-scan">
            <div className="mdc-layout-grid__inner">
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                <label className="examination-label">OCT scan</label>
              </div>
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                <Field
                  idName="right-oct-eye-image"
                  name="right_oct_eye_image"
                  component={imageInput(
                    "Select right eye oct photo file or drop here to upload",
                  )}
                  validate={validateEyeImage}
                  label={values.right_oct_eye_image}
                />
              </div>
              <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                <Field
                  idName="left-oct-eye-image"
                  name="left_oct_eye_image"
                  component={imageInput(
                    "Select left eye oct photo file or drop here to upload",
                  )}
                  validate={validateEyeImage}
                  label={values.left_oct_eye_image}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    );
  };

  return (
    <div className="card wrap-collapsible mb-8">
      <fieldset>
        <div className="card-header">
          <legend>
            <h2>Research participation</h2>
          </legend>
          <p id="consent-question">
            Do you consent to your clinical data being used to improve the care
            of all people with diabetes?
          </p>
          <div className="message-text-wrap">
            KeepSight would like to collect clinical data to be able to support
            academic research. All data used would be de-personalised and could
            not be traced back to individual patients. Our goals are better
            understand how diabetes affects eye health, how clinical information
            obtained during an eye examination can help the overall health of a
            person with diabetes and to improve the KeepSight programme in
            helping people with diabetes maintain good sight.
          </div>
        </div>
        <input
          id="collapsible"
          className="hidden toggle"
          type="checkbox"
          checked={values.research_participant}
          readOnly
          data-testid="research-participant-hidden-checkbox"
        />
        <label
          tabIndex={0}
          htmlFor="collapsible"
          className="lbl-toggle card-band focus:outline focus:outline-offset-2 focus:outline-2 focus:outline-black/80"
          onClick={() => {
            setFieldValue("research_participant", !values.research_participant);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setFieldValue(
                "research_participant",
                !values.research_participant,
              );
            }
          }}
        >
          <div className={switchStyles(values.research_participant)}>
            <div className="mdc-switch__track"></div>
            <div className="mdc-switch__thumb-underlay">
              <div className="mdc-switch__thumb">
                <input
                  type="checkbox"
                  id="optional-switch"
                  className="mdc-switch__native-control no-pointer-events"
                  role="switch"
                  aria-checked={switchRole(values.research_participant)}
                  aria-describedby="consent-question"
                ></input>
              </div>
            </div>
          </div>
          <label
            className="examination-finding-show-button no-pointer-events"
            htmlFor="optional-switch"
          >
            {switchText(values.research_participant)}
          </label>
        </label>
        {renderCollapsibleContent()}
      </fieldset>
    </div>
  );
}

export default ResearchParticipant;
